<template>
  <v-dialog :value="value" max-width="520" persistent @input="onInput">
    <v-card class="force-cancel-dialog">
      <v-card-title class="py-3">
        <v-icon left color="error">mdi-flash-alert</v-icon>
        <span>Force cancel {{ runLabel }}</span>
      </v-card-title>
      <v-divider />

      <v-card-text class="pt-4">
        <v-alert type="warning" dense outlined class="mb-3">
          <strong>Cancel</strong> asks the worker to stop at its next checkpoint and discards
          partial results. <strong>Force cancel</strong> terminates the worker process immediately:
          the run ends as <em>Cancelled</em>, the video returns to its previous status and any work
          in flight is lost.
        </v-alert>

        <div v-if="!graceElapsed" class="caption warning--text mb-3 force-cancel-dialog__grace">
          The cancel request is younger than the grace period ({{ graceSeconds }} s). Forcing now
          skips the cooperative stop.
        </div>

        <div class="body-2 mb-2">
          Type <code class="force-cancel-dialog__word">{{ confirmationWord }}</code> to confirm.
        </div>
        <v-text-field
          v-model="typed"
          dense
          outlined
          autofocus
          hide-details
          class="force-cancel-dialog__input"
          :placeholder="confirmationWord"
          autocomplete="off"
          @keyup.enter="confirm"
        />
      </v-card-text>

      <v-card-actions>
        <v-spacer />
        <v-btn text :disabled="busy" class="force-cancel-dialog__close" @click="close"
          >Keep waiting</v-btn
        >
        <v-btn
          color="error"
          depressed
          :disabled="!canConfirm"
          :loading="busy"
          class="force-cancel-dialog__confirm"
          @click="confirm"
        >
          Force cancel
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import { cancelGraceElapsed, DEFAULT_CANCEL_GRACE_S, sourceLabel } from './queueFormat';

export const FORCE_CONFIRMATION_WORD = 'FORCE';

/**
 * Typed-confirmation dialog for the Force cancel action. Emits `confirm` with
 * `{ run, confirm }` where `confirm` is true when the grace period has not elapsed yet
 * (the API requires `{confirm: true}` in that case).
 */
export default {
  name: 'ForceCancelDialog',

  props: {
    /** v-model: dialog visibility. */
    value: {
      type: Boolean,
      default: false,
    },
    run: {
      type: Object,
      default: null,
    },
    busy: {
      type: Boolean,
      default: false,
    },
    graceSeconds: {
      type: Number,
      default: DEFAULT_CANCEL_GRACE_S,
    },
  },

  data() {
    return {
      typed: '',
      confirmationWord: FORCE_CONFIRMATION_WORD,
    };
  },

  computed: {
    runLabel() {
      if (!this.run) return '';
      const attempt = this.run.attempt != null ? `run ${this.run.attempt} of ` : '';
      return `${attempt}${sourceLabel(this.run)}`;
    },
    graceElapsed() {
      return cancelGraceElapsed(this.run, Date.now(), this.graceSeconds);
    },
    canConfirm() {
      return !this.busy && this.typed.trim().toUpperCase() === this.confirmationWord;
    },
  },

  watch: {
    value(open) {
      if (open) this.typed = '';
    },
  },

  methods: {
    onInput(open) {
      if (!open) this.close();
    },
    close() {
      this.$emit('input', false);
    },
    confirm() {
      if (!this.canConfirm) return;
      this.$emit('confirm', { run: this.run, confirm: !this.graceElapsed });
    },
  },
};
</script>

<style scoped>
.force-cancel-dialog__word {
  font-weight: 600;
}
</style>
